import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '~/api-client';
import { useToast } from '@cad-arc';
import {
    AttachmentsResource,
    UpdateAttachmentRequest,
    UpdateAttachmentResponse,
    AttachmentDto,
    GetAttachmentsResponse,
} from '@mark43/cad-api';
import { logAttachmentsError } from '../helpers';
import { ATTACHMENTS_QUERY_KEY } from '../constants';
import { AttachmentsPanelType } from '../types';

type UseAttachmentsBaseParameter = {
    entityId: number;
    entityType: AttachmentsPanelType;
};

type UpdateAttachmentDescriptionInput = {
    attachmentId: number;
    description: string;
};

type UpdateAttachmentDescriptionResult = {
    response: UpdateAttachmentResponse | undefined;
    attachmentId: number;
    description: string;
};

export function useUpdateAttachment({ entityId }: UseAttachmentsBaseParameter) {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();
    const toast = useToast();

    return useMutation<UpdateAttachmentDescriptionResult, Error, UpdateAttachmentDescriptionInput>({
        mutationFn: async ({
            attachmentId,
            description,
        }: UpdateAttachmentDescriptionInput): Promise<UpdateAttachmentDescriptionResult> => {
            const request: UpdateAttachmentRequest = {
                attachmentIds: [attachmentId],
                description,
            };

            const response =
                await apiClient.request<AttachmentsResource.UpdateAttachmentDescription>({
                    method: 'POST',
                    url: 'attachments/update',
                    data: request,
                });
            return { response: response.data?.data, attachmentId, description };
        },
        onSuccess: ({ attachmentId, description }) => {
            queryClient.setQueryData(
                [ATTACHMENTS_QUERY_KEY, entityId],
                (
                    oldData: GetAttachmentsResponse | undefined
                ): GetAttachmentsResponse | undefined => {
                    if (!oldData) return oldData;

                    const updateAttachmentDescription = (attachments: AttachmentDto[]) => {
                        return attachments.map((attachment) => {
                            if (attachment.attachmentId === attachmentId) {
                                return { ...attachment, description };
                            }
                            return attachment;
                        });
                    };

                    // Update parent attachments array
                    const updatedAttachments = updateAttachmentDescription(oldData.attachments);

                    // Update merged attachments array
                    const updatedMergedAttachments = updateAttachmentDescription(
                        oldData.mergedAttachments
                    );

                    return {
                        attachments: updatedAttachments,
                        mergedAttachments: updatedMergedAttachments,
                    };
                }
            );
        },
        onError: (error) => {
            logAttachmentsError(error as Error, 'update-attachment');
            toast({
                status: 'error',
                description: 'Failed to update attachment',
            });
            queryClient.invalidateQueries([ATTACHMENTS_QUERY_KEY, entityId]);
        },
    });
}
